import { DateString, Icon } from "client/Components";
import {
	ReactElement,
	RefObject,
	useMemo,
	useRef,
	useState
} from "react";
import { HubItemStatistic } from "../common";
import {
	isNil,
	setTrackingId
} from "client/Misc/utils";
import { calculateAmountOfListItemThatFitInTheContainer } from "client/Misc/domUtils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import classNames from "classnames";
import { TimeZone } from "client/DateAndTime/timezones";
import { useResizeObserver } from "client/Hooks";
import { DateFormat } from "client_legacy/ndaw/dateFormatting";
import styles from "./CardBaseInfo.module.less";
import { CategoryList } from "client/Features/Hub/CategoryList";

export type CardBaseInfoProps = {
	startDate: string | Date;
	dateFormat: DateFormat;
	timezone: TimeZone;
	statistics: readonly HubItemStatistic[];
	shouldHideSecondaryInfo: boolean | undefined;
	categoryNames: readonly string[];
};

export const CardBaseInfo = (props: CardBaseInfoProps): ReactElement => {
	const {
		startDate,
		dateFormat,
		timezone,
		statistics,
		shouldHideSecondaryInfo,
		categoryNames
	} = props;

	const infoListRef: RefObject<HTMLUListElement | null> = useRef<HTMLUListElement>(null);
	const { width, wasInitialized } = useResizeObserver(infoListRef);

	const categoriesToShowCount: number = !shouldHideSecondaryInfo
		? categoryNames.length
		: 0;

	const [visibleCategoryCount, setVisibleCategoryCount] = useState<number>(categoriesToShowCount);

	const shouldShowCategories: boolean = !shouldHideSecondaryInfo && categoriesToShowCount !== 0;

	const infoItems = useMemo(
		(): ReactElement[] => {
			const items: ReactElement[] = [
				<>
					<Icon value="calendar" />
					<span {...setTrackingId("start-date")}>
						<DateString
							value={startDate}
							format={dateFormat}
							timezone={timezone}
						/>
					</span>
				</>
			];

			for (const statistic of statistics) {
				items.push(
					<span
						{...setTrackingId(statistic.trackingId)}
						className={styles.statistic}
					>
						<Icon value={statistic.icon} />
						<span {...setTrackingId("value")}>{statistic.text}</span>
					</span>
				);
			};

			return items;
		},
		[
			dateFormat,
			startDate,
			statistics,
			timezone
		]
	);

	const visibleInfoItemCount = useMemo(
		(): number => {
			if (
				isNil(infoListRef.current)
				|| width === 0
				|| !wasInitialized
			) {
				return 0;
			}

			return Math.max(
				1,
				calculateAmountOfListItemThatFitInTheContainer(
					infoListRef.current,
					null,
					width,
					infoItems.length
				)
			);
		},
		[
			infoItems.length,
			wasInitialized,
			width
		]
	);

	const infoListMaxWidth = useMemo(
		(): number | undefined => {
			hasImplicitDependencyOn(infoItems, width);

			if (isNil(infoListRef.current) || !wasInitialized) {
				return undefined;
			}

			const listItemsWidth: readonly number[] = Array.from(
				infoListRef.current
					.querySelectorAll("li")
			)
				.map(el => el.offsetWidth);

			const totalWidth: number = listItemsWidth.reduce((a, b) => a + b, 0);

			return totalWidth === 0
				? undefined
				: totalWidth;
		},
		[
			infoItems,
			wasInitialized,
			width
		]
	);

	return (
		<div className={styles.root}>
			<ul
				className={styles.infoList}
				style={{
					"--card-base-info-list-max-width": infoListMaxWidth
				}}
				ref={infoListRef}
			>
				{infoItems.map(
					(x, index) => {
						return (
							<li
								key={index}
								className={
									classNames(
										styles.infoListItem,
										{
											[styles.infoListItem_hidden]: index >= visibleInfoItemCount
										}
									)
								}
							>
								{x}
							</li>
						);
					}
				)}
			</ul>
			{shouldShowCategories && (
				<div className={styles.categoriesSection}>
					<span className={
						classNames(
							styles.categoriesIcon,
							{
								[styles.categoriesIcon_hidden]: visibleCategoryCount === 0
							}
						)
					}>
						<Icon value="note-sticky" />
					</span>
					<div className={styles.categoryList}>
						<CategoryList
							categories={categoryNames}
							shouldHideCounter={visibleCategoryCount === 0}
							onVisibleItemCountChange={setVisibleCategoryCount}
						/>
					</div>
				</div>
			)}
		</div>
	);
};

CardBaseInfo.displayName = "CardBase.CardBaseInfo";
